<template>
  <div class="full-height">
    <a-spin :spinning="isLoading">
      <div class="chart-container">
        <div class="chart-title">
          {{ statesChartTm('title') }}
        </div>
        <EmptyChart v-if="option.series[0].data.length === 0" />
        <v-chart
          v-else
          class="chart"
          :option="option"
          autoresize
        />
      </div>
    </a-spin>
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { PieChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import { getStatesPercents } from '@/graphql/statistic'
import EmptyChart from '@/components/EmptyChart'

use([
  SVGRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
])

const legendData = [
  '顺利',
  '拖延',
  '风险',
  '放弃'
]

export default defineComponent({
  name: 'StatesChart',
  components: {
    VChart,
    EmptyChart
  },
  provide: {
    [THEME_KEY]: 'light'
  },
  props: {
    departmentIds: {
      type: Array,
      required: true
    }
  },
  setup (props) {
    const { state } = useStore()
    const { tm } = useI18n()
    const cycleId = computed(() => state.selectedCycleId)
    const lang = computed(() => state.app.lang)
    const isLoading = ref(false)
    let seriesData

    const option = reactive({
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      },
      legend: {
        top: '5%',
        left: 'center',
        data: legendData.map(item => statesChartTm(item))
      },
      color: [
        '#42A5F5',
        '#FFCC80',
        '#FF8A65',
        '#A9A9A9'
      ],
      series: [
        {
          name: '状态分析',
          type: 'pie',
          radius: ['50%', '60%'],
          center: ['50%', '60%'],
          data: [],
          label: {
            alignTo: 'edge',
            formatter: '{b}: {c}\n{percent|{d}%}',
            minMargin: 5,
            edgeDistance: 10,
            lineHeight: 15,
            rich: {
              percent: {
                fontSize: 12,
                color: '#999'
              }
            }
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    })

    loadData()

    watch([
      cycleId,
      () => props.departmentIds
    ], loadData)

    watch(lang, () => {
      option.legend.data = legendData.map(item => statesChartTm(item))
      option.series[0].data = seriesData.map(item => ({
        name: statesChartTm(item.name),
        value: item.value
      }))
    })

    function loadData () {
      if (cycleId.value === null || props.departmentIds.length === 0) {
        return
      }
      isLoading.value = true
      getStatesPercents(cycleId.value, props.departmentIds)
        .then(res => {
          seriesData = res.data.map(item => ({
            name: item.keyResultStateName,
            value: parseInt(item.keyResultCount)
          }))
          option.series[0].data = seriesData.map(item => ({
            name: statesChartTm(item.name),
            value: item.value
          }))
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    function statesChartTm (name) {
      return tm('statistic.statesChart.' + name)
    }

    return {
      isLoading,
      option,
      statesChartTm
    }
  }
})
</script>

<style lang="less" scoped>
.chart {
  height: 300px;
  max-width: 600px;
  margin: 0 auto;
}

.full-height {
  height: 100%;

  :deep(.ant-spin-nested-loading) {
    height: 100%;
  }

  :deep(.ant-spin-container) {
    height: 100%;
  }
}
</style>
